export type Selection = Record<string, string | null>;

export type GrantOutcome = { ok: true } | { ok: false; error: string };

export type GrantOutcomes = Record<string, GrantOutcome>;

export interface GrantSummaryEntry {
  systemName: string;
  role: string;
  replaced: string[];
}

function sameRole(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

export function isHeld(held: string[], role: string): boolean {
  return held.some((owned) => sameRole(owned, role));
}

export function grantableRoles(roleNames: string[], held: string[]): string[] {
  return roleNames.filter((role) => !isHeld(held, role));
}

export function defaultRole(roleNames: string[], held: string[]): string | null {
  const options = grantableRoles(roleNames, held);
  return options.length === 1 ? options[0] : null;
}

export function toggleSystem(
  selection: Selection,
  systemId: string,
  roleNames: string[],
  held: string[],
): Selection {
  if (systemId in selection) {
    const next = { ...selection };
    delete next[systemId];
    return next;
  }
  return { ...selection, [systemId]: defaultRole(roleNames, held) };
}

export function chooseRole(selection: Selection, systemId: string, role: string): Selection {
  return { ...selection, [systemId]: role };
}

export function selectedIds(selection: Selection): string[] {
  return Object.keys(selection);
}

export function allRolesChosen(selection: Selection): boolean {
  const ids = selectedIds(selection);
  return ids.length > 0 && ids.every((id) => selection[id] !== null);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function progressLabel(current: number, total: number): string {
  return `Granting ${current} of ${total}…`;
}

export function failedIds(outcomes: GrantOutcomes): string[] {
  return Object.keys(outcomes).filter((id) => !outcomes[id].ok);
}

export function succeededIds(outcomes: GrantOutcomes): string[] {
  return Object.keys(outcomes).filter((id) => outcomes[id].ok);
}

export function successToast(entries: GrantSummaryEntry[]): string {
  if (entries.length === 1) {
    const [entry] = entries;
    return entry.replaced.length > 0
      ? `${entry.role} replaced ${entry.replaced.join(', ')} on ${entry.systemName}`
      : `${entry.role} access to ${entry.systemName} granted`;
  }
  return `Access to ${pluralize(entries.length, 'system')} granted`;
}
